import { Alert, Box, List, ListItem, ListItemText, Typography } from "@mui/material";
import { useIntl } from "../../../../contexts/IntlContext";
import { LivePeriodResult } from "../../../../hooks/useLivePeriod";

type Props = {
  preview: LivePeriodResult | null;
  loading: boolean;
  error: string | null;
};

const formatDateBR = (date: string) => {
  const [y, m, d] = String(date || "").split("-");
  if (!y || !m || !d) return date;
  return `${d}/${m}/${y}`;
};

const formatTime = (value: string) => String(value || "").slice(11, 16) || value;

/**
 * Pré-visualização antes de confirmar: quantos dias serão gerados, e para os dias com mais de 9h,
 * avisa que o sistema vai dividir em Parte 1/Parte 2 com o horário de corte calculado.
 */
export default function LivePeriodPreviewSummary({ preview, loading, error }: Props) {
  const intl = useIntl();

  if (loading) {
    return (
      <Typography variant="body2" color="text.secondary">
        {intl.formatMessage({ id: "liveStreams.period.previewLoading" })}
      </Typography>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!preview || !preview.days.length) {
    return null;
  }

  const splitDays = preview.days.filter((day) => day.parts.length > 1);

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {intl.formatMessage(
          { id: "liveStreams.period.daysCount" },
          { count: preview.days.length },
        )}
      </Typography>
      {typeof preview.billedAmount === "number" && (
        <Typography variant="body2" fontWeight={700} gutterBottom>
          {intl.formatMessage(
            { id: "liveStreams.estimatedPrice" },
            {
              price: new Intl.NumberFormat(intl.locale, {
                style: "currency",
                currency: "BRL",
                currencyDisplay: intl.locale === "pt-BR" ? "symbol" : "code",
              }).format(preview.billedAmount),
            },
          )}
        </Typography>
      )}
      {splitDays.length > 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {intl.formatMessage(
            { id: "liveStreams.period.willSplitWarning" },
            { count: splitDays.length },
          )}
        </Alert>
      )}
      <List dense disablePadding>
        {preview.days.map((day) => (
          <ListItem key={day.date} disableGutters>
            <ListItemText
              primary={formatDateBR(day.date)}
              secondary={
                day.parts.length > 1
                  ? `Parte 1: ${formatTime(day.parts[0].start)}–${formatTime(day.parts[0].end)} · Parte 2: ${formatTime(day.parts[1].start)}–${formatTime(day.parts[1].end)}`
                  : `${formatTime(day.parts[0]?.start ?? "")}–${formatTime(day.parts[0]?.end ?? "")}`
              }
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
